// ============================================
// Login Page — đăng nhập web quản trị qua /auth/admin/login/
// + 2FA mã 6 số qua EMAIL (sheet 2 mục #3, docs/ADMIN_2FA_EMAIL.md)
// ============================================

import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ADMIN_ROUTES } from '@conai/shared-constants';
import { isAdminTwoFactorChallenge, type LoginResponse } from '@conai/shared-types';
import { isApiError } from '@conai/api-client';
import { useAuthStore } from '../../stores';
import { useAuthService } from '../../providers/ApiProvider';
import { tokenStorage } from '../../config/api';
import { getDeviceToken, setDeviceToken } from '../../utils/deviceToken';
import { useI18n } from '../../i18n';

/** Kết quả dịch một lỗi API: câu hiện cho người dùng + có phải quay về bước mật khẩu không. */
type MappedError = { message: string; backToPassword?: boolean };

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setUser = useAuthStore((state) => state.setUser);
  const authService = useAuthService();
  const { t } = useI18n();

  const [email, setEmail] = useState('admin@demo.conai.vn');
  const [password, setPassword] = useState('admin123');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);

  /**
   * Khác null = đã qua bước mật khẩu, đang chờ mã email (mục #3).
   *
   * Giữ trong state của component chứ KHÔNG cất vào storage: phiên chờ sống
   * 10 phút và chỉ có nghĩa trong đúng lần đăng nhập này. Cất đi thì tải lại
   * trang sẽ khôi phục một màn nhập mã với token gần như chắc chắn đã chết.
   *
   * ⚠️ "Gửi lại mã" trả về token MỚI và huỷ cái cũ — luôn ghi đè.
   */
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [emailHint, setEmailHint] = useState('');
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  /** Thời điểm mã hết hạn (ms epoch) — để đếm ngược `code_expires_in`. */
  const [codeDeadline, setCodeDeadline] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (codeDeadline === null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [codeDeadline]);

  const secondsLeft =
    codeDeadline === null ? 0 : Math.max(0, Math.ceil((codeDeadline - now) / 1000));
  const countdown = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;

  // Get the redirect URL from location state
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || ADMIN_ROUTES.DASHBOARD;

  /**
   * Dịch lỗi API sang câu cho người dùng — phân nhánh bằng `error_code`,
   * KHÔNG đổ thẳng `message` (tiếng Anh, chỉ để đọc log) ra màn hình.
   *
   * Hai ca chưa có error_code (mục #5 lời nhắn BE):
   * - 401 ở bước 1: bắt riêng chuỗi máy "ACCOUNT_UNVERIFIED", còn lại gom về
   *   một câu chung (sai mật khẩu / tài khoản khoá).
   * - 429: `retry_after` nằm ở CẤP CAO NHẤT của body (không trong `data`),
   *   không có header Retry-After, và có thể bằng 0.
   */
  const mapApiError = (err: unknown, phase: 'login' | 'code'): MappedError => {
    if (isApiError(err)) {
      const { status, code: errorCode } = err;

      if (status === 429) {
        const retryAfter = (err.data as { retry_after?: unknown } | undefined)?.retry_after;
        const seconds = typeof retryAfter === 'number' && retryAfter > 0 ? retryAfter : 0;
        return {
          message:
            seconds > 0
              ? t('adminLoginRateLimited', { seconds: String(seconds) })
              : t('adminLoginRateLimitedGeneric'),
        };
      }
      if (errorCode === 'ADMIN_LOGIN_MAIL_FAILED' || status === 503) {
        return { message: t('adminLoginMailFailed') };
      }
      if (errorCode === 'ADMIN_LOGIN_ROLE_NOT_ALLOWED') {
        return { message: t('adminLoginRoleNotAllowed') };
      }
      if (errorCode === 'ADMIN_LOGIN_NO_EMAIL') {
        return { message: t('adminLoginNoEmail') };
      }
      if (errorCode === 'ADMIN_LOGIN_PENDING_INVALID') {
        // Phiên hết hạn / token đã dùng / tài khoản đổi role giữa hai bước —
        // gõ lại mã là ngõ cụt, phải quay về màn mật khẩu.
        return { message: t('adminTwoFactorSessionExpired'), backToPassword: true };
      }
      if (errorCode === 'ADMIN_LOGIN_CODE_INVALID') {
        return { message: t('adminTwoFactorCodeInvalid') };
      }
      if (status === 401) {
        if (phase === 'login') {
          if (err.message === 'ACCOUNT_UNVERIFIED') {
            return { message: t('adminLoginUnverified') };
          }
          return { message: t('adminLoginInvalidCredentials') };
        }
        return { message: t('adminTwoFactorCodeInvalid') };
      }
      if (status === 400) {
        return { message: t('adminLoginBadRequest') };
      }
    }
    return { message: phase === 'login' ? t('loginFailed') : t('adminTwoFactorCodeInvalid') };
  };

  const backToPassword = (message = '') => {
    setPendingToken(null);
    setEmailHint('');
    setCode('');
    setRememberDevice(false);
    setCodeDeadline(null);
    setInfo('');
    setError(message);
  };

  const applyMappedError = (mapped: MappedError) => {
    if (mapped.backToPassword) {
      backToPassword(mapped.message);
    } else {
      setError(mapped.message);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Chặn double-submit: gọi bước 1 lần nữa sẽ HUỶ mã đang chờ — hai tab hay
    // một cú double-click là người dùng gõ mã của mail đến trước trong khi chỉ
    // mã của mail đến sau còn sống. Đây là ca "mã không đúng" hay gặp nhất.
    if (isSubmitting) return;
    setError('');
    setInfo('');
    setIsSubmitting(true);

    try {
      // Set remember me preference before login so tokens go to correct storage
      tokenStorage.setRememberMe(rememberMe);

      // Gửi kèm X-Device-Token nếu trình duyệt này từng được tin cậy — BE sẽ
      // bỏ qua bước mã. Cờ 2FA đang tắt thì BE cũng trả token thẳng ở đây.
      const outcome = await authService.adminLogin(
        { identifier: email, password },
        { deviceToken: getDeviceToken() }
      );

      // Đòi mã: HTTP 200 nhưng KHÔNG có token, chỉ có `pending_token`. Phải
      // dừng ở đây — người dùng nhập mật khẩu ĐÚNG, chưa hề có lỗi.
      if (isAdminTwoFactorChallenge(outcome)) {
        setPendingToken(outcome.pending_token);
        setEmailHint(outcome.email_hint);
        setCodeDeadline(Date.now() + outcome.code_expires_in * 1000);
        setCode('');
        setRememberDevice(false);
        return;
      }

      await completeLogin(outcome);
    } catch (err) {
      applyMappedError(mapApiError(err, 'login'));
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Phần đuôi dùng chung cho cả đăng nhập thẳng lẫn đăng nhập qua mã email. */
  const completeLogin = async (data: LoginResponse) => {
    // Backward-compatible safeguard for mock server payload shape
    const legacyPayload = data as unknown as {
      tokens?: { accessToken?: string; refreshToken?: string };
    };
    if (legacyPayload.tokens?.accessToken && legacyPayload.tokens?.refreshToken) {
      await tokenStorage.setAccessToken(legacyPayload.tokens.accessToken);
      await tokenStorage.setRefreshToken(legacyPayload.tokens.refreshToken);
    }

    const user = data.user as unknown as {
      full_name?: string;
      fullName?: string;
      is_active?: boolean;
      isActive?: boolean;
      created_at?: string;
      createdAt?: string;
      updated_at?: string;
      updatedAt?: string;
    } & typeof data.user;

    setUser({
      ...user,
      full_name: user.full_name ?? user.fullName ?? 'Unknown User',
      is_active: user.is_active ?? user.isActive ?? true,
      created_at: user.created_at ?? user.createdAt ?? new Date().toISOString(),
      updated_at: user.updated_at ?? user.updatedAt ?? new Date().toISOString(),
    });

    navigate(from, { replace: true });
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingToken || isSubmitting) return;
    setError('');
    setInfo('');
    setIsSubmitting(true);

    try {
      const data = await authService.adminLoginVerify({
        pending_token: pendingToken,
        code: code.trim(),
        remember_device: rememberDevice,
      });

      // Chỉ có khi người dùng tích "không hỏi lại trên thiết bị này" — lưu
      // localStorage và gửi qua header ở mọi lần đăng nhập sau. Không dựa vào
      // cookie của BE: cookie cross-site chỉ sống trên HTTPS.
      if (data.device_token) {
        setDeviceToken(data.device_token);
      }

      await completeLogin(data);
    } catch (err) {
      const mapped = mapApiError(err, 'code');
      applyMappedError(mapped);
      if (!mapped.backToPassword) {
        setCode('');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!pendingToken || isSubmitting || isResending) return;
    setError('');
    setInfo('');
    setIsResending(true);

    try {
      const data = await authService.adminLoginResend({ pending_token: pendingToken });
      // GHI ĐÈ pending_token bằng token mới — đồng hồ pending chạy từ bước 1
      // và không tự gia hạn; giữ token cũ thì mã mới tinh trong hộp thư sẽ đi
      // kèm một phiên đã hết hạn.
      setPendingToken(data.pending_token);
      if (data.email_hint) setEmailHint(data.email_hint);
      setCodeDeadline(Date.now() + data.code_expires_in * 1000);
      setCode('');
      setInfo(t('adminTwoFactorResent'));
    } catch (err) {
      applyMappedError(mapApiError(err, 'code'));
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 px-4">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-slate-800">{t('conaiStore')}</h1>
          <p className="mt-2 text-slate-600">{t('adminPortal')}</p>
        </div>

        {/* Bước 2 — nhập mã 6 số từ email, thay hẳn form mật khẩu (mục #3) */}
        {pendingToken ? (
          <div className="rounded-2xl bg-white p-8 shadow-xl">
            <h2 className="text-2xl font-bold text-slate-900">{t('adminTwoFactorTitle')}</h2>
            <p className="mt-2 text-sm text-slate-600">
              {t('adminTwoFactorSubtitle', { email: emailHint })}
            </p>

            {error && (
              <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
            )}
            {info && (
              <div className="mt-4 rounded-lg bg-green-50 p-3 text-sm text-green-700">{info}</div>
            )}

            <form onSubmit={handleCodeSubmit} className="mt-6 space-y-4">
              <div>
                <label htmlFor="twoFactorCode" className="block text-sm font-medium text-slate-700">
                  {t('adminTwoFactorCodeLabel')}
                </label>
                <input
                  id="twoFactorCode"
                  // inputMode numeric + autocomplete one-time-code: điện thoại
                  // gợi ý mã ngay từ bàn phím, đỡ phải chuyển app qua lại.
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2.5 text-center text-lg font-mono tracking-[0.4em] text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  placeholder="000000"
                  required
                />
                <p className="mt-2 text-xs text-slate-500">
                  {secondsLeft > 0
                    ? t('adminTwoFactorExpiresIn', { time: countdown })
                    : t('adminTwoFactorExpired')}
                </p>
              </div>

              {/* Ô tích quyết định `remember_device`. Không tích thì BE không
                  ghi nhớ gì — máy dùng chung ở quầy không nên thành thiết bị
                  tin cậy. */}
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-slate-600">{t('adminTwoFactorRemember')}</span>
              </label>

              <button
                type="submit"
                disabled={isSubmitting || code.length < 6}
                className="w-full rounded-lg bg-blue-600 px-4 py-2.5 font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
              >
                {isSubmitting ? t('loading') : t('twoFactorVerify')}
              </button>

              <button
                type="button"
                onClick={handleResend}
                disabled={isSubmitting || isResending}
                className="w-full text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-60"
              >
                {isResending ? t('loading') : t('adminTwoFactorResend')}
              </button>

              <button
                type="button"
                onClick={() => backToPassword()}
                className="w-full text-sm font-medium text-slate-500 hover:text-slate-700"
              >
                {t('twoFactorBack')}
              </button>
            </form>
          </div>
        ) : (
        /* Login Card */
        <div className="rounded-2xl bg-white p-8 shadow-xl">
          <h2 className="text-2xl font-bold text-slate-900">{t('loginTitle')}</h2>
          <p className="mt-2 text-sm text-slate-600">
            {t('loginSubtitle')}
          </p>

          {/* Error Message */}
          {error && (
            <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Demo Credentials Info */}
            <div className="mt-4 rounded-lg bg-blue-50 p-3 text-sm text-blue-700">
            <p className="font-medium">{t('demoAccount')}</p>
            <p className="mt-1">Email: admin@demo.conai.vn</p>
            <p>{t('password')}: admin123</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="your@email.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                {t('password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="••••••••"
                required
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-slate-600">{t('rememberMe')}</span>
              </label>
              <Link
                to={ADMIN_ROUTES.FORGOT_PASSWORD}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                {t('forgotPassword')}
              </Link>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {t('processing')}
                </span>
              ) : (
                t('signIn')
              )}
            </button>
          </form>

          {/* Register Link */}
          <p className="mt-6 text-center text-sm text-slate-600">
            {t('noAccount')}{' '}
            <Link to="/register" className="font-medium text-blue-600 hover:text-blue-700">
              {t('registerNow')}
            </Link>
          </p>
        </div>
        )}

        {/* Footer */}
        <p className="mt-8 text-center text-xs text-slate-500">
          © 2024 ConAI. {t('footerCopyright')}
        </p>
      </div>
    </div>
  );
}
